import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CreditCard, Lock, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface MockPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingData: {
    tutorName: string;
    subject: string;
    date: string;
    time: string;
    amount: number;
  };
  onPaymentComplete: (bookingId: string) => void;
}

const MockPaymentModal = ({ isOpen, onClose, bookingData, onPaymentComplete }: MockPaymentModalProps) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const navigate = useNavigate();

  const handleMockPayment = async () => {
    setIsProcessing(true);
    
    // Simulate payment processing
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setIsProcessing(false);
    setIsComplete(true);
    
    // Simulate booking confirmation
    setTimeout(() => {
      const mockBookingId = 'booking_' + Date.now();
      onPaymentComplete(mockBookingId);
      setIsComplete(false);
      onClose();
    }, 1500);
  };

  const handleSkipPayment = () => {
    const mockBookingId = 'booking_' + Date.now();
    onPaymentComplete(mockBookingId);
    onClose();
  };

  if (isComplete) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="sr-only">Payment Complete</DialogTitle>
          </DialogHeader>
          <div className="text-center py-8">
            <CheckCircle className="mx-auto h-16 w-16 text-green-500 mb-4" />
            <h3 className="text-xl font-semibold text-green-600 mb-2">Payment Successful!</h3>
            <p className="text-gray-600">Your booking has been confirmed</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <CreditCard className="mr-2 h-5 w-5" />
            Complete Your Booking
          </DialogTitle>
          <DialogDescription>
            This is a mock payment screen for testing purposes
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Booking Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Booking Summary</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Tutor:</span>
                <span className="font-medium">{bookingData.tutorName}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Subject:</span>
                <span className="font-medium">{bookingData.subject}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Date:</span>
                <span className="font-medium">{bookingData.date}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Time:</span>
                <span className="font-medium">{bookingData.time}</span>
              </div>
              <hr />
              <div className="flex justify-between font-semibold text-lg">
                <span>Total:</span>
                <span className="text-green-600">${bookingData.amount}</span>
              </div>
            </CardContent>
          </Card>

          {/* Mock Payment Form */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center">
                <Lock className="mr-2 h-4 w-4" />
                Payment Information (Mock)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="cardNumber">Card Number</Label>
                <Input 
                  id="cardNumber" 
                  placeholder="4242 4242 4242 4242" 
                  value="4242 4242 4242 4242"
                  disabled
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="expiry">Expiry</Label>
                  <Input 
                    id="expiry" 
                    placeholder="12/25" 
                    value="12/25"
                    disabled
                  />
                </div>
                <div>
                  <Label htmlFor="cvc">CVC</Label>
                  <Input 
                    id="cvc" 
                    placeholder="123" 
                    value="123"
                    disabled
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="name">Cardholder Name</Label>
                <Input 
                  id="name" 
                  placeholder="John Doe" 
                  value="John Doe"
                  disabled
                />
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="space-y-3">
            <Button 
              onClick={handleMockPayment}
              disabled={isProcessing}
              className="w-full bg-green-600 hover:bg-green-700"
            >
              {isProcessing ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Processing Payment...
                </>
              ) : (
                <>
                  <CreditCard className="mr-2 h-4 w-4" />
                  Pay ${bookingData.amount} (Mock)
                </>
              )}
            </Button>
            
            <Button 
              onClick={handleSkipPayment}
              variant="outline"
              className="w-full"
              disabled={isProcessing}
            >
              Skip Payment & Book Free Session
            </Button>
            
            <Button 
              onClick={onClose}
              variant="ghost"
              className="w-full"
              disabled={isProcessing}
            >
              Cancel
            </Button>
          </div>

          <div className="text-xs text-center text-gray-500">
            This is a mock payment interface for testing. No real payment will be processed.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MockPaymentModal;
